import { api } from '@/utils/api';
import { controle } from '@/features/salaNoCelular/salaNoCelular';
import { ponteDoAplicativo, type SalaPreparada } from './aplicativo';
import { aparelhoDaSala, apagarDoAparelho, salasDoAparelho } from './salaNoAparelho';
import { comoEncerrar, itensDaNoticia, pendentesDe, type ItemDaFila, type SalaNoPortal } from './regrasDoEncerrar';

export { comoEncerrar, salasEsquecidas, type ItemDaFila, type SalaNoPortal } from './regrasDoEncerrar';

/**
 * "ENCERRAR SALA" NO APARELHO, E A NOTÍCIA AO PORTAL (03/11/2026; pedido do pesquisador depois das salas 30, 49 e 53,
 * ACTIVE por dias). O servidor é `backend/src/services/salaEsquecida.ts`. Só telas: usa o que a ponte do computador
 * (2.0.20+) e o motor do celular (1.6) já sabem — listar, enviar, apagar.
 *
 *   A NOTÍCIA (`contarAoPortal`): as salas DA CONTA neste aparelho, como a página as vê (aberta, iniciada, encerrada, a
 *   enviar), a cada abertura e a cada minuto (`NoticiaDasSalas.tsx`). O portal só sabia da sala pelo envio.
 *   O BOTÃO (`encerrarNoAparelho`), pela situação da sala aqui (`comoEncerrar`):
 *     aberta agora, ou iniciada e não encerrada → abre a sala: o fim é o "Encerrar para todos" de lá (o motor de hoje não
 *       encerra a sala iniciada sem abrir — fica para o nativo da 2.0.22/1.7);
 *     com resultado a enviar → envia, e só encerra depois de o portal receber; nunca apaga o que falta enviar;
 *     o resto → encerra no portal e apaga a prova do aparelho.
 *   SEM INTERNET: sem nada a enviar, apaga do aparelho e guarda "encerrar no portal" NESTE aparelho (`FILA`); a próxima
 *   notícia com internet manda. O cartão diz "Sala encerrada — aguardando enviar ao portal".
 *   O AVISO (`salasEsquecidas`): sala da conta, preparada ou aberta aqui, com o prazo do simulado vencido.
 *
 * A LIBERAÇÃO: o botão, o aviso e o "Encerrar sala" do portal só aparecem quando o servidor diz `encerrarSala: true`
 * (`ENCERRAR_SALA_LIBERADO_PARA`). A notícia vale sempre.
 */

// ─── o que a página guarda: a última resposta do portal e a fila sem internet ───

const CHAVE_DA_FILA = 'salas:a-encerrar-no-portal';
export function lerFila(): ItemDaFila[] {
  try { const v = JSON.parse(localStorage.getItem(CHAVE_DA_FILA) ?? '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
}
function gravarFila(f: ItemDaFila[]) { try { localStorage.setItem(CHAVE_DA_FILA, JSON.stringify(f)); } catch { /* sem armazenamento: a rotina do servidor encerra depois do prazo */ } avisar(); }

let ultima: { salas: SalaNoPortal[]; encerrarSala: boolean } = { salas: [], encerrarSala: false };
const ouvintes = new Set<() => void>();
function avisar() { ouvintes.forEach((f) => f()); }
export function assinar(f: () => void) { ouvintes.add(f); return () => { ouvintes.delete(f); }; }
export const ultimaNoticia = () => ultima;

const semRede = (e: unknown) => !(e as { response?: unknown })?.response;

/** A notícia: conta ao portal as salas da conta neste aparelho; depois, manda a fila de encerramentos. */
export async function contarAoPortal(userId: number): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  const salas = await salasDoAparelho().catch(() => null);
  if (!salas) return;
  const aparelho = await aparelhoDaSala().catch(() => null);
  const { data } = await api.post<{ salas: SalaNoPortal[]; encerrarSala: boolean }>('/api/offline-rooms/aplicativo/noticia',
    { salas: itensDaNoticia(salas, userId), completa: true },
    { headers: aparelho ? { 'X-Portal-Simulados-Aparelho': aparelho.id } : {} });
  ultima = { salas: data.salas ?? [], encerrarSala: !!data.encerrarSala };
  avisar();
  for (const item of lerFila().filter((x) => x.userId === userId)) {
    try { await api.post(`/api/offline-rooms/${item.id}/encerrar`, { porque: 'botao' }); gravarFila(lerFila().filter((x) => x.id !== item.id)); }
    catch (e) {
      if (semRede(e)) return; // sem internet: fica para a próxima
      // O portal recusou (outra conta, sala que não existe, entrega no aparelho por notícia velha): sai da fila, e a
      // rotina do servidor cuida dela depois do prazo — a prova já não está neste aparelho.
      gravarFila(lerFila().filter((x) => x.id !== item.id));
    }
  }
}

export type Resultado = { ok: true; texto: string } | { ok: false; texto: string };

/** Espera o envio terminar (o celular manda na hora; o computador envia sozinho a cada minuto). Teto: 2 minutos. */
async function enviarEEsperar(id: number): Promise<SalaPreparada | null> {
  if (!ponteDoAplicativo()) await controle('POST', '/enviar', { id }).catch(() => null);
  for (let i = 0; i < 24; i++) {
    const s = (await salasDoAparelho().catch(() => [])).find((x) => x.id === id) ?? null;
    if (!s || (pendentesDe(s) ?? 0) === 0) return s;
    await new Promise((ok) => setTimeout(ok, 5000));
  }
  return (await salasDoAparelho().catch(() => [])).find((x) => x.id === id) ?? null;
}

/**
 * "Encerrar sala" no aparelho. Nunca apaga o que falta enviar: com pendente, envia primeiro e, se não foi, para e diz.
 * `abrir` é quem abre a sala na tela (o cartão sabe navegar), para o caso da sala iniciada.
 */
export async function encerrarNoAparelho(sala: SalaPreparada, userId: number, abrir: () => void): Promise<Resultado> {
  let s: SalaPreparada | null = sala;
  if (comoEncerrar(s) === 'abrir') { abrir(); return { ok: false, texto: 'A sala foi iniciada: encerre na tela da sala, com "Encerrar para todos". Os resultados vão ao portal em seguida.' }; }
  if (comoEncerrar(s) === 'enviar-antes') {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return { ok: false, texto: 'Esta sala tem resultados a enviar. Conecte à internet: o aplicativo envia e depois encerra. Nada foi apagado.' };
    s = await enviarEEsperar(sala.id);
    const falta = s ? pendentesDe(s) ?? 0 : 0;
    if (falta > 0) return { ok: false, texto: `Ainda há ${falta} ${falta === 1 ? 'item' : 'itens'} a enviar. Confira a internet e tente de novo; o aplicativo continua tentando sozinho. A sala não foi encerrada e nada foi apagado.` };
  }
  const item: ItemDaFila = { id: sala.id, userId, simuladoId: sala.simuladoId, titulo: sala.titulo, desde: new Date().toISOString() };
  let noPortal: 'sim' | 'depois' = 'depois';
  if (typeof navigator === 'undefined' || navigator.onLine !== false) {
    try {
      // A notícia antes: o portal recusa quando a última notícia diz que há entrega aqui — e ela pode ser velha.
      await contarAoPortal(userId).catch(() => { /* sem a notícia, o encerrar diz */ });
      await api.post(`/api/offline-rooms/${sala.id}/encerrar`, { porque: 'botao' });
      noPortal = 'sim';
    } catch (e) {
      if (!semRede(e)) return { ok: false, texto: (e as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'O portal não encerrou a sala. Nada foi apagado.' };
    }
  }
  if (s) {
    const apagada = await apagarDoAparelho(s.id, false);
    if (!apagada.ok) return { ok: false, texto: `${noPortal === 'sim' ? 'A sala foi encerrada no portal, mas a prova continua neste aparelho: ' : ''}${apagada.erro}` };
  }
  if (noPortal === 'depois') { gravarFila([...lerFila().filter((x) => x.id !== item.id), item]); return { ok: true, texto: 'Sala encerrada neste aparelho. O portal fica sabendo na próxima conexão.' }; }
  return { ok: true, texto: 'Sala encerrada. A prova saiu deste aparelho.' };
}
